import { useEffect, useRef, useState } from "react";

const LightAnimation = () => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const timeRef = useRef(0);
  const [mousePos, setMousePos] = useState({ x: 150, y: 150 });
  const [isMouseDown, setIsMouseDown] = useState(false);
  const [isMouseOverCanvas, setIsMouseOverCanvas] = useState(false);
  const [transitionProgress, setTransitionProgress] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Intersection Observer to only animate when visible
    const observer = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          setIsVisible(entry.isIntersecting);
        }
      },
      { threshold: 0.1 }, // Start when 10% visible
    );

    observer.observe(canvas);

    // Cleanup observer
    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    // Only start animation when component is visible
    if (!isVisible) {
      // Clean up any existing animation
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = undefined;
      }
      return;
    }

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    // Set canvas size
    canvas.width = 300;
    canvas.height = 300;

    // Light streams and arcs
    const lightStreams = [];
    const maxStreams = 12; // Reduced to 12 for better performance

    // Initialize light streams
    for (let i = 0; i < maxStreams; i++) {
      lightStreams.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 3,
        vy: (Math.random() - 0.5) * 3,
        length: (Math.random() * 40 + 20) * 2, // Doubled length
        width: (Math.random() * 3 + 1) * 2, // Doubled width
        hue: Math.random() * 60 + 200, // Blue to purple range
        intensity: Math.random() * 0.5 + 0.5,
        angle: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 0.1,
        trail: [],
        sizeMultiplier: 1, // New property for size scaling
      });
    }

    // Mouse event handlers
    const handleMouseMove = e => {
      const rect = canvas.getBoundingClientRect();
      setMousePos({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
    };

    const handleMouseDown = () => setIsMouseDown(true);
    const handleMouseUp = () => setIsMouseDown(false);
    const handleMouseEnter = () => setIsMouseOverCanvas(true);
    const handleMouseLeave = () => setIsMouseOverCanvas(false);

    canvas.addEventListener("mousemove", handleMouseMove);
    canvas.addEventListener("mousedown", handleMouseDown);
    canvas.addEventListener("mouseup", handleMouseUp);
    canvas.addEventListener("mouseenter", handleMouseEnter);
    canvas.addEventListener("mouseleave", handleMouseLeave);

    const animate = () => {
      const time = timeRef.current * 0.001;

      // Smooth transition for mouse enter/leave
      const targetTransition = isMouseOverCanvas ? 1 : 0;
      setTransitionProgress(prev => {
        const diff = targetTransition - prev;
        return prev + diff * 0.08; // Smooth transition speed
      });

      // Canvas with dark blue background
      ctx.fillStyle = "rgba(0, 0, 68, 0.15)";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Update and draw light streams
      for (const stream of lightStreams) {
        // Update position
        stream.x += stream.vx;
        stream.y += stream.vy;

        // Bounce off edges
        if (stream.x < 0 || stream.x > canvas.width) stream.vx *= -1;
        if (stream.y < 0 || stream.y > canvas.height) stream.vy *= -1;

        // Calculate distance to mouse
        const dx = mousePos.x - stream.x;
        const dy = mousePos.y - stream.y;
        const distance = Math.hypot(dx, dy);

        // Handle size changes based on mouse state
        stream.sizeMultiplier =
          isMouseDown && distance < 120
            ? Math.min(5, stream.sizeMultiplier + 0.1) // Grow lights when clicking near them
            : Math.max(1, stream.sizeMultiplier - 0.008); // Gradually shrink lights when not clicking

        // Determine visibility and intensity based on mouse position with smooth transitions
        let visibility;
        let intensity;

        if (transitionProgress > 0.1) {
          // When transitioning to focused mode
          if (distance < 80) {
            // Lights near mouse are fully visible and explosively bright
            visibility = 1;
            intensity = Math.min(5, stream.intensity + ((80 - distance) / 80) * 4);

            // Attract lights to mouse
            const force = (80 - distance) / 80;
            stream.vx += dx * force * 0.03;
            stream.vy += dy * force * 0.03;
          } else {
            // Lights far from mouse fade away gradually based on transition progress
            const fadeDistance = 80 + (200 - 80) * (1 - transitionProgress);
            if (distance < fadeDistance) {
              const fadeFactor = (fadeDistance - distance) / (fadeDistance - 80);
              visibility = fadeFactor * transitionProgress;
              intensity = stream.intensity * fadeFactor * transitionProgress;
            } else {
              visibility = 0;
              intensity = 0;
            }
          }
        } else {
          // When not focused, show all lights normally
          visibility = 1;
          intensity = stream.intensity;
        }

        // Skip rendering if not visible
        if (visibility <= 0) {
          continue;
        }

        // Add trail points
        stream.trail.push({ x: stream.x, y: stream.y, time: time });
        if (stream.trail.length > 20) {
          stream.trail.shift();
        }

        // Draw light trail with size multiplier
        if (stream.trail.length > 1) {
          ctx.save();
          ctx.globalCompositeOperation = "screen";

          // Create flowing light trail
          for (let i = 1; i < stream.trail.length; i++) {
            const alpha = (i / stream.trail.length) * intensity * visibility * 2;
            const width = (i / stream.trail.length) * stream.width * 4 * stream.sizeMultiplier;

            ctx.strokeStyle = `hsla(${stream.hue}, 100%, 70%, ${alpha})`;
            ctx.lineWidth = width;
            ctx.lineCap = "round";

            ctx.beginPath();
            ctx.moveTo(stream.trail[i - 1].x, stream.trail[i - 1].y);
            ctx.lineTo(stream.trail[i].x, stream.trail[i].y);
            ctx.stroke();

            // Add explosive glow effect
            ctx.shadowColor = `hsl(${stream.hue}, 100%, 70%)`;
            ctx.shadowBlur = width * 6;
            ctx.stroke();
            ctx.shadowBlur = 0;
          }

          ctx.restore();
        }

        // Draw main light stream with size multiplier
        ctx.save();
        ctx.globalCompositeOperation = "screen";

        // Create light arc
        const endX = stream.x + Math.cos(stream.angle) * stream.length * stream.sizeMultiplier;
        const endY = stream.y + Math.sin(stream.angle) * stream.length * stream.sizeMultiplier;

        // Draw main light beam
        const gradient = ctx.createLinearGradient(stream.x, stream.y, endX, endY);
        gradient.addColorStop(0, `hsla(${stream.hue}, 100%, 90%, ${intensity * visibility})`);
        gradient.addColorStop(0.5, `hsla(${stream.hue}, 100%, 80%, ${intensity * visibility * 0.9})`);
        gradient.addColorStop(1, `hsla(${stream.hue}, 100%, 60%, ${intensity * visibility * 0.5})`);

        ctx.strokeStyle = gradient;
        ctx.lineWidth = stream.width * 5 * stream.sizeMultiplier;
        ctx.lineCap = "round";

        ctx.beginPath();
        ctx.moveTo(stream.x, stream.y);
        ctx.lineTo(endX, endY);
        ctx.stroke();

        // Add explosive glow effect
        ctx.shadowColor = `hsl(${stream.hue}, 100%, 80%)`;
        ctx.shadowBlur = stream.width * 12 * stream.sizeMultiplier;
        ctx.stroke();
        ctx.shadowBlur = 0;

        // Draw light source point with size multiplier
        ctx.fillStyle = `hsla(${stream.hue}, 100%, 95%, ${intensity * visibility})`;
        ctx.beginPath();
        ctx.arc(stream.x, stream.y, stream.width * 1.5 * stream.sizeMultiplier, 0, Math.PI * 2);
        ctx.fill();

        ctx.restore();

        // Update stream angle
        stream.angle += stream.rotationSpeed;

        // Limit velocity
        stream.vx = Math.max(-4, Math.min(4, stream.vx));
        stream.vy = Math.max(-4, Math.min(4, stream.vy));
      }

      // Draw light connections between nearby streams (only when visible)
      ctx.save();
      ctx.globalCompositeOperation = "screen";
      ctx.strokeStyle = "rgba(100, 150, 255, 0.1)";
      ctx.lineWidth = 1;

      for (let i = 0; i < lightStreams.length; i++) {
        for (let j = i + 1; j < lightStreams.length; j++) {
          const dx = lightStreams[i].x - lightStreams[j].x;
          const dy = lightStreams[i].y - lightStreams[j].y;
          const distance = Math.hypot(dx, dy);

          if (distance < 80) {
            // Only draw connections if both streams are visible
            const stream1Distance = Math.hypot(mousePos.x - lightStreams[i].x, mousePos.y - lightStreams[i].y);
            const stream2Distance = Math.hypot(mousePos.x - lightStreams[j].x, mousePos.y - lightStreams[j].y);

            if (transitionProgress > 0.1 && (stream1Distance > 80 || stream2Distance > 80)) {
              // Gradually fade connections based on transition progress
              const fadeDistance = 80 + (200 - 80) * (1 - transitionProgress);
              if (stream1Distance > fadeDistance || stream2Distance > fadeDistance) {
                continue; // Skip if either stream is too far
              }
            }

            const alpha = ((80 - distance) / 80) * 0.2 * transitionProgress;
            ctx.strokeStyle = `rgba(100, 150, 255, ${alpha})`;
            ctx.beginPath();
            ctx.moveTo(lightStreams[i].x, lightStreams[i].y);
            ctx.lineTo(lightStreams[j].x, lightStreams[j].y);
            ctx.stroke();
          }
        }
      }
      ctx.restore();

      timeRef.current += 16; // 60 FPS
      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      canvas.removeEventListener("mousemove", handleMouseMove);
      canvas.removeEventListener("mousedown", handleMouseDown);
      canvas.removeEventListener("mouseup", handleMouseUp);
      canvas.removeEventListener("mouseenter", handleMouseEnter);
      canvas.removeEventListener("mouseleave", handleMouseLeave);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mousePos, isMouseDown, isMouseOverCanvas, isVisible]);

  return (
    <div style={{ textAlign: "center" }}>
      <canvas
        ref={canvasRef}
        style={{
          border: "none",
          cursor: "pointer",
          background: "#004",
        }}
      />
      <div
        style={{
          position: "relative",
          display: "inline-block",
          top: "-29px",
          width: "150px",
          padding: "8px",
          fontSize: "12px",
          color: "#fff",
          background: "rgba(0, 0, 0, 0.2)",
          zIndex: 100,
        }}
      >
        Made with AI
      </div>
    </div>
  );
};

export default LightAnimation;
